import {
    ICashierService,
    OpenCashierOptions,
    OpenDepositOptions,
    OpenTransactionHistoryOptions,
    OpenWithdrawOptions
} from "../../../common/interfaces/cashier.service.interface";
import {ServiceBase} from "../ServiceBase";
import {AppIdentifiers, CashierSessionType, ProductPackageToID} from "@sparkware/uc-sdk-core";
import {isCashoutSessionType, isDepositSessionType} from "../../../common/utils/cashier-util";
import {IActionHandlerEventParams} from "../../../common/interfaces/casinofied-analytics-proxy.interface";
import {
    UserEligibilityController_CashoutWidgetEligibilityRequest,
    UserEligibilityController_CashoutWidgetEligibilityResponse,
    UserEligibilityController_DepositWidgetEligibilityRequest,
    UserEligibilityController_DepositWidgetEligibilityResponse,
    UserEligibilityController_TransactionHistoryWidgetEligibilityRequest,
    UserEligibilityController_TransactionHistoryWidgetEligibilityResponse
} from "../api/api.service";
import {IGeoLocationInfo} from "../../../common/interfaces/geo-location-info.interface";
import {
    PendingActionResultEnum
} from "../external-links/handlers/pending-action/pending-action.external-link-handler.interface";
import {DialogButtonClickHandleResultEnum} from "../dialog/dialog.service.interface";
import {FeatureToggleStatusEnum, IFeatureToggleViewModel} from "../ab-testing/feature-toggle.view-model.interface";


interface IEligibilityCheckResult {
    blockedByPendingAction: boolean;
    isEligible: boolean;
    businessCorrelationID: string;
    abCheckActionHandlerEventData?: IActionHandlerEventParams;
    eligibilityActionHandlerEventData?: IActionHandlerEventParams;
}

type UnifiedCashierWidgetEligibilityResponse =
    UserEligibilityController_TransactionHistoryWidgetEligibilityResponse |
    UserEligibilityController_DepositWidgetEligibilityResponse |
    UserEligibilityController_CashoutWidgetEligibilityResponse;

type UnifiedCashierWidgetEligibilityRequest =
    UserEligibilityController_TransactionHistoryWidgetEligibilityRequest |
    UserEligibilityController_DepositWidgetEligibilityRequest |
    UserEligibilityController_CashoutWidgetEligibilityRequest;

type INTSEligibilityCallback = (request: UnifiedCashierWidgetEligibilityRequest) => Promise<UnifiedCashierWidgetEligibilityResponse>;

export class CashierService extends ServiceBase implements ICashierService {

    getProductPackage(): ProductPackageToID {
        if (this.services.device.os.isMobileOS) {
            return ProductPackageToID.FULL_CASHIER_MOBILE;
        } else {
            return ProductPackageToID.FULL_CASHIER_PC;
        }
    }

    private _reportEligibilityEvents(eligibilityResult: IEligibilityCheckResult): void {
        if (eligibilityResult.abCheckActionHandlerEventData) {
            this.services.casinofiedAnalytics.actionHandler(eligibilityResult.abCheckActionHandlerEventData);
        }

        if (eligibilityResult.eligibilityActionHandlerEventData) {
            this.services.casinofiedAnalytics.actionHandler(eligibilityResult.eligibilityActionHandlerEventData);
        }
    }


    async isEligibleForDepositWidget(): Promise<boolean> {
        const eligibility = await this._getDepositWidgetEligibility();
        return eligibility.isEligible;
    }

    async isCashierInRedirectMode(): Promise<boolean> {
        return this.services.device.os.isMobileOS && !(await this.isEligibleForDepositWidget());
    }

    private async _evaluateNTSEligibility(appName: AppIdentifiers, ntsEligibilityCallback: INTSEligibilityCallback): Promise<UnifiedCashierWidgetEligibilityResponse> {
        try {
            let geoInfo: IGeoLocationInfo;
            try {
                geoInfo = await this.services.sdk.getGeoLocationInfo();
            }
            catch (geoError) {
                this.services.logger.error(`Failed to get geo info for ${appName} Widget Eligibility`, geoError);
            }
            return await ntsEligibilityCallback({
                LoginCountry: this.services.sdk.getCountryIso3(),
                StateID: geoInfo?.stateId
            });
        } catch (err) {
            this.services.logger.error(`Failed to call ${appName} Widget Eligibility`, err);
            return {
                isEligible: false,
                ineligibleReason: "error",
                errorDescription: `Failed to query NTS ${appName} eligibility`,
                errorID: 1,
                hasErrors: true
            }
        }
    }

    private async _getWidgetEligibility(appName: AppIdentifiers,
                                        analyticsActionName: string,
                                        checkPendingActions: () => Promise<PendingActionResultEnum>,
                                        getFeatureToggle: () => IFeatureToggleViewModel,
                                        ntsEligibilityCallback: INTSEligibilityCallback): Promise<IEligibilityCheckResult> {


        if (this.services.miniOrbit.isEnabled) {
            // In mini orbit, cashier is handled directly by the app.
            // For example, in poker, cashier is opened directly by the poker app
            return {
                isEligible: false,
                businessCorrelationID: "",
                blockedByPendingAction: false
            };
        }

        const widgetConfigs = this.services.configuration.servicesConfigs.AppLauncher.apps[appName];

        if (widgetConfigs.featureToggle === FeatureToggleStatusEnum.Disabled) {
            return {
                isEligible: false,
                businessCorrelationID: "",
                blockedByPendingAction: false
            };
        }

        const featureToggle = getFeatureToggle();

        if(this.services.configuration.servicesConfigs.PendingActions.enableExplicitCheck && featureToggle.isEnabled) {
            const pendingActionResult = await checkPendingActions();
            if(pendingActionResult !== PendingActionResultEnum.ContinueCurrentFlow) {
                return {
                    isEligible: false,
                    blockedByPendingAction: true,
                    businessCorrelationID: ""
                }
            }
        }


        const businessCorrelationID = this.services.tools.getGuid();

        let abCheckActionHandlerEventData: IActionHandlerEventParams | null = null;
        let featureABData = featureToggle.getFeatureABData();
        if(featureABData) {
            abCheckActionHandlerEventData = {
                businessCorrelationID: businessCorrelationID,
                actionName: analyticsActionName,
                actionHandlerStep: 'ab_test_step',
                actionResult: {
                    featureName: featureABData.FeatureName,
                    testGroup: featureABData.GroupName,
                    testID: featureABData.TestId
                }
            };
        }


        let ntsEligibility = await this._evaluateNTSEligibility(appName, ntsEligibilityCallback);
        const eligibilityActionHandlerEventData: IActionHandlerEventParams = {
            businessCorrelationID: businessCorrelationID,
            actionName: analyticsActionName,
            actionHandlerStep: 'eligibility_check_step',
            actionResult: {
                isEligible: ntsEligibility.isEligible,
                ineligibleReason: ntsEligibility.ineligibleReason,
                errorCode: ntsEligibility.errorID,
                errorMessage: ntsEligibility.errorDescription,
                hasError: Boolean(ntsEligibility.errorID)
            }
        };

        return {
            isEligible: featureToggle.isEnabled && ntsEligibility.isEligible,
            businessCorrelationID: businessCorrelationID,
            abCheckActionHandlerEventData: abCheckActionHandlerEventData,
            eligibilityActionHandlerEventData: eligibilityActionHandlerEventData,
            blockedByPendingAction: false
        };
    }

    private async _getDepositWidgetEligibility(): Promise<IEligibilityCheckResult> {
        //On mini orbit, we want Sport/Poker to open Deposit widget, so Eligibility checks are performed by them.
        if (this.services.miniOrbit.isEnabled) {
            return Promise.resolve(
                {
                    isEligible: true,
                    businessCorrelationID: this.services.tools.getGuid(),
                    blockedByPendingAction: false
                }
            );
        }

        return this._getWidgetEligibility(
            AppIdentifiers.UnifiedCashier,
            'deposit',
            async () => await (await this.services.externalLinks).openDepositPendingAction(),
            () => this.services.abTestingFeatureDataProvider.depositWidget,
            (params: UnifiedCashierWidgetEligibilityRequest) => this.services.api.userEligibilityController.depositWidgetEligibility(params)
        );
    }

    private async _getCashOutWidgetEligibility(): Promise<IEligibilityCheckResult> {
        return this._getWidgetEligibility(
            AppIdentifiers.UnifiedCashout,
            'withdraw',
            () => Promise.resolve(PendingActionResultEnum.ContinueCurrentFlow),
            () => this.services.abTestingFeatureDataProvider.cashoutWidget,
            (params: UnifiedCashierWidgetEligibilityRequest) => this.services.api.userEligibilityController.cashoutWidgetEligibility(params)
        );
    }

    private async _getTransactionHistoryWidgetEligibility(): Promise<IEligibilityCheckResult> {
        return this._getWidgetEligibility(
            AppIdentifiers.TransactionHistory,
            'transaction_history',
            async () => await (await this.services.externalLinks).openTransactionHistoryPendingAction(),
            () => this.services.abTestingFeatureDataProvider.transactionHistoryWidget,
            (params: UnifiedCashierWidgetEligibilityRequest) => this.services.api.userEligibilityController.transactionHistoryWidgetEligibility(params)
        );
    }


    async openDeposit(options: OpenDepositOptions): Promise<void> {

        if(!await this.services.user.ensureUserIsLoggedIn('CashierService.openDeposit')) {
            return;
        }

        const depositEligibility = await this._getDepositWidgetEligibility();

        if(depositEligibility.blockedByPendingAction) {
            return;
        }

        this._reportEligibilityEvents(depositEligibility);

        if (depositEligibility.isEligible) {
            await (await this.services.appLauncher).openDepositWidget({
                businessCorrelationID: depositEligibility.businessCorrelationID || this.services.tools.getGuid(),
                channel: options.channel,
                appSpecificParameters: {
                    promoCode: options?.PromCode
                },
                ...options.launchInfo
            });
        } else {
            await this.openOldCashier({
                sessionType: CashierSessionType.QuickDeposit,
                ...options
            });
        }
    }

    async openWithdraw(options: OpenWithdrawOptions): Promise<void> {
        if(!await this.services.user.ensureUserIsLoggedIn('CashierService.openWithdraw')) {
            return;
        }

        const cashOutWidgetEligibility = await this._getCashOutWidgetEligibility();

        if(cashOutWidgetEligibility.blockedByPendingAction) {
            return;
        }

        this._reportEligibilityEvents(cashOutWidgetEligibility);

        if(this.services.configuration.servicesConfigs.AppLauncher.apps.PAYMENTS_CASHOUT.redirectToVerificationModuleForNonEligible) {

            if(cashOutWidgetEligibility.eligibilityActionHandlerEventData?.actionResult?.hasError) {
                this.services.dialog.showSimpleTextModal({
                    text: this.services.regulation.getGeneralErrorMessage(),
                    showEmptyTitle: true,
                    buttons: [
                        {
                            text: this.services.translation.translate('OK'),
                            buttonPreset: 'primary',
                            onClick: async () => DialogButtonClickHandleResultEnum.CloseDialogAndContinue
                        }
                    ]
                });
                return;
            }

            if(!cashOutWidgetEligibility.isEligible) {
                await (await this.services.externalLinks).openUploadDocumentFromMyAccount();
                return;
            }
        }

        if (cashOutWidgetEligibility.isEligible) {
            await (await this.services.appLauncher).openCashoutWidget({
                businessCorrelationID: cashOutWidgetEligibility.businessCorrelationID || this.services.tools.getGuid(),
                channel: options.channel,
                sequentialCorrelationID: 1,
                ...options.launchInfo
            });
        } else {
            await this.openOldCashier({
                sessionType: CashierSessionType.CashOutDepositHistory,
                ...options
            });
        }
    }

    async openTransactionHistory(options: OpenTransactionHistoryOptions): Promise<void> {
        if(!await this.services.user.ensureUserIsLoggedIn('CashierService.openTransactionHistory')) {
            return;
        }

        const transactionHistoryWidgetEligibility = await this._getTransactionHistoryWidgetEligibility();

        if(transactionHistoryWidgetEligibility.blockedByPendingAction) {
            return;
        }

        this._reportEligibilityEvents(transactionHistoryWidgetEligibility);

        if (transactionHistoryWidgetEligibility.isEligible) {
            await (await this.services.appLauncher).openTransactionHistoryWidget({
                businessCorrelationID: transactionHistoryWidgetEligibility.businessCorrelationID || this.services.tools.getGuid(),
                channel: options.channel,
                ...options.launchInfo
            });
        } else {
            await this.openOldCashier({
                sessionType: CashierSessionType.TransactionHistory,
                ...options
            });
        }
    }

    async openCashier(options: OpenCashierOptions = {}): Promise<void> {
        if(!await this.services.user.ensureUserIsLoggedIn('CashierService.openCashier')) {
            return;
        }

        if (isDepositSessionType(options.sessionType)) {
            const depositEligibility = await this._getDepositWidgetEligibility();
            if(depositEligibility.blockedByPendingAction) {
                return;
            }

            this._reportEligibilityEvents(depositEligibility);
            if (depositEligibility.isEligible) {
                await (await this.services.appLauncher).openDepositWidget({
                    businessCorrelationID: depositEligibility.businessCorrelationID || this.services.tools.getGuid(),
                    channel: options.channel,
                    appSpecificParameters: {
                        promoCode: options?.PromCode || ""
                    }
                });
                return;
            }
        } else if (isCashoutSessionType(options.sessionType)) {
            const cashOutEligibility = await this._getCashOutWidgetEligibility();
            if(cashOutEligibility.blockedByPendingAction) {
                return;
            }

            this._reportEligibilityEvents(cashOutEligibility);
            if (cashOutEligibility.isEligible) {
                await (await this.services.appLauncher).openCashoutWidget({
                    businessCorrelationID: cashOutEligibility.businessCorrelationID || this.services.tools.getGuid(),
                    channel: options.channel,
                });
                return;
            }
        }

        await this._openCashierExternalLink(options);
    }

    async openOldCashier(options: OpenCashierOptions = {}): Promise<void> {
        if(!await this.services.user.ensureUserIsLoggedIn('CashierService.openOldCashier')) {
            return;
        }
        await this._openCashierExternalLink(options);
    }

    private async _openCashierExternalLink(options: OpenCashierOptions = {}): Promise<void> {
        await (await this.services.externalLinks).openCashier(options);
    }

    async openUploadDocumentsFromCashier(): Promise<void> {
        return this._openCashierExternalLink({
            sessionType: CashierSessionType.VerifyId
        });
    }

}